<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar class="custom-toolbar">
        <ion-buttons slot="start">
          <ion-menu-button color="light"></ion-menu-button>
        </ion-buttons>
        <ion-title class="custom-title">Verify Field Visits</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="fetchPendingVerifications" color="light" class="refresh-btn">
            <ion-icon slot="icon-only" :icon="refreshOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="verify-content">
      <div class="verify-container">
        
        <!-- Summary Box -->
        <div v-if="!loading && logs.length > 0" class="pending-summary">
          <ion-icon :icon="notificationsOutline" class="summary-icon"></ion-icon>
          <span>You have <strong>{{ logs.length }}</strong> pending field visit logs requiring verification.</span>
        </div>

        <!-- Loader -->
        <div v-if="loading" class="loading-state">
          <ion-spinner name="crescent" color="primary"></ion-spinner>
          <span>Fetching verification requests...</span>
        </div>

        <!-- Verification Cards List -->
        <div v-else-if="logs.length > 0" class="verification-list">
          <div 
            v-for="log in logs" 
            :key="log.id" 
            class="verification-card"
            :class="pushFocusClass(log.id)"
            :data-push-id="log.id"
          >
            <!-- Top Gradient Status line based on Log Type (Check In/Out) -->
            <div class="card-glow" :class="log.log_type"></div>

            <div class="card-header-row">
              <div class="employee-info">
                <div class="avatar-badge">
                  {{ getInitials(log.user ? log.user.name : 'Employee') }}
                </div>
                <div>
                  <h3 class="employee-name">{{ log.user ? log.user.name : 'Unknown Employee' }}</h3>
                  <span class="employee-sub">Field Visit Verification</span>
                </div>
              </div>
              <div class="action-type-badge" :class="log.log_type">
                <ion-icon :icon="log.log_type === 'in' ? logInOutline : logOutOutline" class="type-icon"></ion-icon>
                <span>CHECK {{ log.log_type.toUpperCase() }}</span>
              </div>
            </div>

            <div class="card-body">
              <!-- Bento Information Grid -->
              <div class="details-bento">
                <div class="bento-cell">
                  <span class="cell-label">TIME OF LOG</span>
                  <span class="cell-val">{{ formatDateTime(log.log_time) }}</span>
                </div>
                <div class="bento-cell">
                  <span class="cell-label">COORDINATES</span>
                  <span class="cell-val">{{ formatCoordinate(log.latitude) }}, {{ formatCoordinate(log.longitude) }}</span>
                </div>
                <div class="bento-cell full-width" v-if="log.field_visit_purpose">
                  <span class="cell-label">VISIT PURPOSE</span>
                  <span class="cell-val italic-purpose">"{{ log.field_visit_purpose }}"</span>
                </div>
                <div class="bento-cell full-width">
                  <span class="cell-label">DEVICE & HARDWARE</span>
                  <span class="cell-val device-text">{{ cleanDeviceInfo(log.device_info) }}</span>
                </div>
              </div>

              <!-- Open Source Static Map Component -->
              <div class="map-container">
                <span class="map-label">
                  <ion-icon :icon="pinOutline"></ion-icon>
                  <span>Logged Location Map</span>
                </span>
                <div class="map-wrapper">
                  <img 
                    :src="getStaticMapUrl(log.latitude, log.longitude)" 
                    class="static-map-img" 
                    alt="Field Location Map"
                    loading="lazy"
                  />
                  <div class="map-overlay-coordinates">
                    {{ log.location_name || 'Field Location' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Row -->
            <div class="card-actions">
              <button class="btn-action btn-reject" @click="verify(log.id, 'rejected')">
                <ion-icon :icon="closeOutline"></ion-icon>
                <span>Reject Log</span>
              </button>
              <button class="btn-action btn-verify" @click="verify(log.id, 'verified')">
                <ion-icon :icon="checkmarkOutline"></ion-icon>
                <span>Verify & Approve</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State Redesign -->
        <div v-else class="empty-state">
          <div class="empty-icon-wrapper">
            <ion-icon :icon="checkmarkDoneCircleOutline" class="empty-icon"></ion-icon>
          </div>
          <h2>All Logs Verified</h2>
          <p>Great job! There are no pending field visit verification requests from your team members.</p>
        </div>

      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/* eslint-disable */
import { 
  IonButtons, 
  IonContent, 
  IonHeader, 
  IonMenuButton, 
  IonPage, 
  IonTitle, 
  IonToolbar, 
  IonButton, 
  IonIcon, 
  alertController,
  IonSpinner
} from '@ionic/vue';
import { 
  refreshOutline,
  notificationsOutline,
  logInOutline,
  logOutOutline,
  checkmarkOutline,
  closeOutline,
  pinOutline,
  checkmarkDoneCircleOutline
} from 'ionicons/icons';
import Loading from '../../Loading';
import { pushFocus } from '../../pushNotifications';

export default {
  name: 'FieldVisitsVerify',
  mixins: [pushFocus],
  components: {
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonButton,
    IonIcon,
    IonSpinner
  },
  data() {
    return {
      logs: [],
      loading: true,
      // Icons
      refreshOutline,
      notificationsOutline,
      logInOutline,
      logOutOutline,
      checkmarkOutline,
      closeOutline,
      pinOutline,
      checkmarkDoneCircleOutline
    }
  },
  created() {
    this.fetchPendingVerifications();
  },
  methods: {
    reloadForPush() {
      this.fetchPendingVerifications();
    },
    formatCoordinate(val) {
      if (val === undefined || val === null) return '0.00000';
      const num = parseFloat(val);
      return isNaN(num) ? '0.00000' : num.toFixed(5);
    },
    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return '';
      try {
        const date = new Date(dateTimeStr);
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true });
      } catch (e) {
        return dateTimeStr;
      }
    },
    getInitials(name) {
      if (!name) return 'EE';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    cleanDeviceInfo(info) {
      if (!info) return 'Unknown Device';
      // Simple parse to make user agents human readable
      if (info.includes('iPhone')) return 'Apple iPhone (iOS)';
      if (info.includes('Android')) {
        const match = info.match(/Android\s([0-9\.]+)/);
        const version = match ? `(v${match[1]})` : '';
        return `Android Device ${version}`;
      }
      if (info.includes('Macintosh')) return 'macOS Workstation';
      if (info.includes('Windows')) return 'Windows Workstation';
      return info.split(' ')[0] || 'Web Browser';
    },
    getStaticMapUrl(lat, lng) {
      // Keyless and fast OpenStreetMap-based static maps API provider (Yandex static maps with red point marker)
      return `https://static-maps.yandex.ru/1.x/?ll=${lng},${lat}&size=450,180&z=15&l=map&pt=${lng},${lat},pm2rdm`;
    },
    async presentAlert(title, message) {
      const alert = await alertController.create({
        header: title,
        message: message,
        buttons: ['OK'],
      });
      await alert.present();
    },
    fetchPendingVerifications() {
      this.loading = true;
      const data = {
        username: window.localStorage.username,
        password: window.localStorage.password,
      };

      this.$http.post('hr/fieldVisitsForVerification', data, { skipLoading: true })
        .then(response => {
          if (response.data.status) {
            this.logs = response.data.logs;
          }
          this.loading = false;
          // Opened from a notification: show that visit.
          this.focusPushCard('This field visit has already been verified.');
        })
        .catch(err => {
          console.error(err);
          this.loading = false;
        });
    },
    async verify(logId, status) {
      const confirmAlert = await alertController.create({
        header: 'Confirm Verification',
        message: `Are you sure you want to mark this field visit as ${status}?`,
        buttons: [
          { text: 'Cancel', role: 'cancel' },
          {
            text: status === 'verified' ? 'Approve' : 'Reject',
            handler: async () => {
              const loader = await Loading.presentLoading('Processing...');
              const data = {
                username: window.localStorage.username,
                password: window.localStorage.password,
                log_id: logId,
                status: status,
              };

              this.$http.post('hr/verifyFieldVisit', data)
                .then(response => {
                  loader.dismiss();
                  if (response.data.status) {
                    this.presentAlert('Success', response.data.message);
                    this.fetchPendingVerifications();
                  } else {
                    this.presentAlert('Failed', response.data.message);
                  }
                })
                .catch(err => {
                  loader.dismiss();
                });
            }
          }
        ]
      });
      await confirmAlert.present();
    }
  }
}
</script>

<style scoped>
/* Core Styles */
.verify-content {
  --background: #f4f6fc;
}

.verify-container {
  padding: 16px;
  max-width: 600px;
  margin: 0 auto;
}

/* Custom Toolbar Styles */
.custom-toolbar {
  --background: linear-gradient(135deg, #102a43 0%, #243b53 100%);
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-title {
  font-weight: 700;
  font-size: 1.15rem;
  letter-spacing: 0.5px;
}

.refresh-btn {
  --color: #fff;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 12px;
  color: #627d98;
}

/* Summary Banner */
.pending-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 12px 16px;
  border-radius: 16px;
  color: #1e40af;
  font-size: 0.86rem;
  margin-bottom: 16px;
}

.summary-icon {
  font-size: 1.25rem;
  color: #2563eb;
  flex-shrink: 0;
}

.pending-summary strong {
  font-weight: 700;
}

/* Verification Card */
.verification-card {
  position: relative;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.02);
  margin-bottom: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease;
}

.verification-card:active {
  transform: scale(0.99);
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
}

.card-glow.in {
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
}

.card-glow.out {
  background: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
}

/* Header Row */
.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}

.employee-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-badge {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  box-shadow: 0 4px 10px rgba(99, 102, 241, 0.2);
}

.employee-name {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #0f172a;
}

.employee-sub {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.action-type-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}

.action-type-badge.in {
  background: #d1fae5;
  color: #065f46;
}

.action-type-badge.out {
  background: #dbeafe;
  color: #1e40af;
}

.type-icon {
  font-size: 0.9rem;
}

/* Card Body */
.card-body {
  padding: 16px;
}

/* Bento Info Grid */
.details-bento {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.bento-cell {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
}

.bento-cell.full-width {
  grid-column: span 2;
}

.cell-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.cell-val {
  font-size: 0.85rem;
  font-weight: 700;
  color: #334e68;
}

.italic-purpose {
  font-style: italic;
  color: #0f172a;
}

.device-text {
  font-size: 0.78rem;
  color: #475569;
}

/* Static Map Styles */
.map-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.map-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
}

.map-wrapper {
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #cbd5e1;
}

.static-map-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background-color: #e2e8f0;
}

.map-overlay-coordinates {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(15, 23, 42, 0.85);
  color: #fff;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 600;
  backdrop-filter: blur(4px);
  max-width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Action Buttons Footer */
.card-actions {
  display: flex;
  gap: 10px;
  padding: 12px 16px 16px 16px;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}

.btn-action {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 12px;
  padding: 12px;
  font-size: 0.88rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: transform 0.15s;
}

.btn-action:active {
  transform: scale(0.97);
}

.btn-reject {
  background: #fee2e2;
  color: #ef4444;
}

.btn-verify {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  margin-top: 48px;
}

.empty-icon-wrapper {
  background: #d1fae5;
  padding: 20px;
  border-radius: 50%;
  margin-bottom: 16px;
}

.empty-icon {
  font-size: 3rem;
  color: #059669;
}

.empty-state h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #102a43;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 0.88rem;
  color: #627d98;
  margin: 0;
  max-width: 300px;
  line-height: 1.5;
}
</style>
